import moment from "moment";
import { memo, PureComponent } from "react";
import { Grey8 } from "styles/antTheme/Colors";

import { BTStatusBadge } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { isInPortal } from "utilities/portal/portal";

import {
    BTIconInfoCircleErrorOutlined,
    BTIconSyncOutlined,
} from "commonComponents/btWrappers/BTIcon";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { DateDisplay } from "commonComponents/utilities/DateDisplay/DateDisplay";
import { StatusDisplay, type StatusTypes } from "commonComponents/utilities/Status/StatusDisplay";
import { StatusTagDisplay } from "commonComponents/utilities/Status/StatusTagDisplay";

export enum InvoiceStatus {
    Unreleased = 1,
    PendingReleased = 2,
    PartiallyPaid = 3,
    Paid = 4,
    Void = 5,
    PastDue = 6,
    Scheduled = 7,
}

export const InvoiceStatusDescription = new Map<InvoiceStatus, string>([
    [InvoiceStatus.Unreleased, "Draft"],
    [InvoiceStatus.PendingReleased, isInPortal({ builder: true }) ? "Pending/Sent" : "Pending"],
    [InvoiceStatus.PartiallyPaid, "Partially paid"],
    [InvoiceStatus.Paid, "Paid"],
    [InvoiceStatus.Void, "Void"],
    [InvoiceStatus.PastDue, "Past due"],
    [InvoiceStatus.Scheduled, "Scheduled"],
]);

export const InvoiceStatusType = (status: InvoiceStatus): StatusTypes => {
    switch (status) {
        case InvoiceStatus.PendingReleased:
            return "pending";
        case InvoiceStatus.PartiallyPaid:
        case InvoiceStatus.Paid:
            return "success";
        case InvoiceStatus.Void:
        case InvoiceStatus.PastDue:
            return "danger";
        case InvoiceStatus.Scheduled:
            return "info";
        default:
            return "";
    }
};

const getStatusDetails = (
    status: InvoiceStatus,
): { finalStatusType: StatusTypes; finalStatusDesc: string } => {
    const finalStatusType = InvoiceStatusType(status);
    const finalStatusDesc = InvoiceStatusDescription.get(status)! ?? "Draft";
    return {
        finalStatusType,
        finalStatusDesc,
    };
};

const renderDetails = (
    status: InvoiceStatus,
    releasedByDate: moment.Moment | undefined,
    releasedByName: string | undefined,
    scheduledReleaseDate: moment.Moment | undefined,
) => {
    if (releasedByDate) {
        return (
            <>
                Sent {releasedByName ? ` by ${releasedByName} ` : ""}
                on <DateDisplay value={releasedByDate} />
            </>
        );
    }

    if (status === InvoiceStatus.Scheduled && scheduledReleaseDate) {
        return (
            <>
                {`Scheduled to be sent to client on ${scheduledReleaseDate.format(
                    "MMMM D, YYYY, h:mm A z",
                )}`}
            </>
        );
    }

    return undefined;
};

const getBdsDisplayType = (oldStatusType: StatusTypes) => {
    if (!oldStatusType) {
        return "default";
    } else if (oldStatusType === "pending") {
        return "warning";
    } else if (oldStatusType === "emergency") {
        return "danger";
    }
    return oldStatusType;
};

interface IOwnerInvoiceStatusProps {
    status: InvoiceStatus;
    releasedByDate: moment.Moment | undefined;
    releasedByName: string | undefined;
    scheduledReleaseDate: moment.Moment | undefined;
    useBdsBadge?: boolean;
    useSentenceCase?: boolean;
}

const toSentenceCase = (inputString: string) => {
    if (!inputString) {
        return inputString;
    }
    return inputString.charAt(0).toUpperCase() + inputString.slice(1).toLowerCase();
};

export const OwnerInvoiceStatus = memo(function OwnerInvoiceStatusMemo(
    props: IOwnerInvoiceStatusProps,
) {
    const { status, releasedByDate, releasedByName, scheduledReleaseDate, useSentenceCase } = props;
    const details = renderDetails(status, releasedByDate, releasedByName, scheduledReleaseDate);
    const statusDetails = getStatusDetails(status);

    const description = useSentenceCase
        ? toSentenceCase(statusDetails.finalStatusDesc)
        : statusDetails.finalStatusDesc;
    if (props.useBdsBadge) {
        return (
            <BTStatusBadge
                displayType={getBdsDisplayType(statusDetails.finalStatusType)}
                testid="statusBadge"
                text={description}
                textOnly
            />
        );
    }
    return (
        <StatusDisplay
            statusType={statusDetails.finalStatusType}
            details={details}
            displayMode="relative"
            data-testid="invoiceStatusDisplay"
        >
            {description}
        </StatusDisplay>
    );
});

interface IOwnerInvoiceStatusTagProps {
    status: InvoiceStatus;
    popoverContent?: React.ReactNode;
}

export class OwnerInvoiceStatusTag extends PureComponent<IOwnerInvoiceStatusTagProps> {
    render = () => {
        const { status, popoverContent } = this.props;
        return (
            <StatusTagDisplay
                statusType={getStatusDetails(status).finalStatusType}
                statusText={getStatusDetails(status).finalStatusDesc}
                popoverContent={popoverContent}
            />
        );
    };
}

export interface IOwnerInvoicePaymentStatusProps {
    paymentStatusType: InvoiceStatus;
    hasPaymentProcessing: boolean;
    hasIssueWithPayment: boolean;
    "data-testid": string;
    scheduledReleaseDate: string | null;
}

export class OwnerInvoicePaymentStatus extends PureComponent<IOwnerInvoicePaymentStatusProps> {
    render() {
        const {
            paymentStatusType,
            hasIssueWithPayment,
            hasPaymentProcessing,
            "data-testid": testId,
            scheduledReleaseDate,
        } = this.props;

        let hoverText: string | null = null;
        const isScheduled = paymentStatusType === InvoiceStatus.Scheduled && scheduledReleaseDate;

        if (hasIssueWithPayment) {
            hoverText = "Issue with payment";
        } else if (hasPaymentProcessing) {
            hoverText = "Processing";
        } else if (isScheduled) {
            const formattedDate = moment(scheduledReleaseDate).format("MMMM D, YYYY, h:mm A z");
            hoverText = `Scheduled to be sent on ${formattedDate}`;
        }

        let iconDisplay: JSX.Element | null = null;
        if (hasIssueWithPayment) {
            iconDisplay = <BTIconInfoCircleErrorOutlined className="padding-left-xs" />;
        } else if (hasPaymentProcessing) {
            iconDisplay = (
                <BTIconSyncOutlined className="padding-left-xs" style={{ color: Grey8 }} />
            );
        }

        return (
            <div className={cn("flex", "align-items-center")} data-testid={testId}>
                {isScheduled ? (
                    <OwnerInvoiceStatusTag status={paymentStatusType} popoverContent={hoverText} />
                ) : (
                    <BTPopover content={hoverText} disabled={!hoverText}>
                        <OwnerInvoiceStatusTag status={paymentStatusType} />
                        {iconDisplay}
                    </BTPopover>
                )}
            </div>
        );
    }
}
